import { Component, computed, inject, signal } from '@angular/core';
import { loader } from '@/utils/signals/loader';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { routeParam } from '@/utils/signals/router';
import {
  AttachmentDetailDto,
  BuildingEquipmentQrCodeService,
  QrMaintenanceTimerCreateDto,
  QrMaintenanceTimerDto,
  QrRepairTaskSendDto,
  QrSessionDto,
  RepairTaskExternalQrService,
} from '@artemis-software/wr-api';
import { IAlbum, Lightbox } from 'ngx-lightbox';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { firstValueFrom } from 'rxjs';
import {
  RepairTaskCreateDialogComponent,
  RepairTaskCreateDialogResult,
} from '@components/dialog/repair-task-create-dialog/repair-task-create-dialog.component';
import { QrSessionService } from '@services/qr-session.service';
import { QrRepairTaskMailDialogComponent } from './qr-repair-task-mail-dialog/qr-repair-task-mail-dialog.component';

type TimerType = QrMaintenanceTimerCreateDto.TypeEnum;

const CONTRACTOR_TIMER_TYPES: TimerType[] = Object.values(QrMaintenanceTimerCreateDto.TypeEnum)
  .filter(type => type !== QrMaintenanceTimerCreateDto.TypeEnum.Warranty);
const MAX_CREATOR_NAME_LENGTH = 255;

function isDayCount(value: number | undefined, minimum: number): boolean {
  return value !== undefined && Number.isInteger(value) && value >= minimum;
}
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

@Component({
  standalone: false,
  selector: 'wr-qr-code-tga-detail-page',
  templateUrl: './qr-code-tga-detail-page.component.html',
  styleUrls: ['./qr-code-tga-detail-page.component.scss'],
})
export class QrCodeTgaDetailPageComponent {

  readonly loading = loader();
  readonly title = signal('');
  readonly buildingEquipmentId = routeParam('buildingEquipmentId', true);
  readonly buildingEquipmentQrCodeService = inject(BuildingEquipmentQrCodeService);
  readonly repairTaskExternalQrService = inject(RepairTaskExternalQrService);
  readonly qrSessionService = inject(QrSessionService);
  readonly dialogService = inject(NbDialogService);
  readonly toastrService = inject(NbToastrService);
  readonly lightbox = inject(Lightbox);

  readonly session = signal<QrSessionDto | undefined>(undefined);
  readonly creatingRepairTask = signal(false);
  readonly isContractorSession = computed(() => this.session()?.kind === QrSessionDto.KindEnum.Contractor);
  readonly canCreateRepairTask = computed(() => {
    const session = this.session();
    return session?.kind === QrSessionDto.KindEnum.ExternalUser && session.canCreateRepairTasks;
  });

  readonly buildingEquipment = asyncComputed(() => {
    const buildingEquipmentId = this.buildingEquipmentId();
    if (!buildingEquipmentId) {
      return undefined;
    }
    return this.buildingEquipmentQrCodeService.findBuildingEquipmentByBuildingEquipmentId(buildingEquipmentId);
  }, undefined, this.loading);

  readonly publicFolders = asyncComputed(() => {
    const buildingEquipmentId = this.buildingEquipmentId();
    if (!buildingEquipmentId) {
      return undefined;
    }
    return this.buildingEquipmentQrCodeService.findPublicAttachmentFolders(buildingEquipmentId);
  });

  readonly timerOptions = asyncComputed(() => {
    const buildingEquipmentId = this.buildingEquipmentId();
    if (!buildingEquipmentId || !this.session()) {
      return undefined;
    }
    return this.buildingEquipmentQrCodeService.findMaintenanceTimerOptions(buildingEquipmentId);
  }, undefined);
  readonly timerConfigs = computed(() => this.timerOptions()?.lockedConfigs ?? []);
  readonly defaultTimerConfigs = computed(() => this.timerOptions()?.defaultConfigs ?? []);
  readonly timerTypes = CONTRACTOR_TIMER_TYPES;

  readonly timers = asyncComputed(() => {
    const buildingEquipmentId = this.buildingEquipmentId();
    if (!buildingEquipmentId || !this.session()?.canViewMaintenanceTimers) {
      return [];
    }
    return this.buildingEquipmentQrCodeService.findMaintenanceTimers(buildingEquipmentId);
  }, [] as QrMaintenanceTimerDto[]);

  readonly timerFormOpen = signal(false);
  readonly selectedTimerType = signal<TimerType | undefined>(undefined);
  readonly durationDays = signal<number | undefined>(undefined);
  readonly reminderDurationDays = signal<number | undefined>(0);
  readonly earlyToleranceDays = signal<number | undefined>(0);
  readonly lateToleranceDays = signal<number | undefined>(0);
  readonly imagesMandatory = signal(true);
  readonly creatorName = signal('');
  readonly creatingTimer = signal(false);
  readonly today = new Date();
  readonly maxCreatorNameLength = MAX_CREATOR_NAME_LENGTH;

  readonly selectedConfig = computed(() => {
    const type = this.selectedTimerType();
    return this.timerConfigs().find(config => config.timerType === type);
  });

  readonly selectedDefaultConfig = computed(() => {
    const type = this.selectedTimerType();
    return this.defaultTimerConfigs().find(config => config.timerType === type);
  });

  readonly effectiveDurationDays = computed(() => this.selectedConfig()?.durationDays ?? this.durationDays());

  readonly nextDate = computed(() => {
    const durationDays = this.effectiveDurationDays();
    if (durationDays === undefined) {
      return undefined;
    }
    const next = new Date(this.today);
    next.setDate(next.getDate() + durationDays);
    return next;
  });

  readonly ownValuesValid = computed(() => {
    const tolerancesValid = this.isContractorSession()
      || (isDayCount(this.earlyToleranceDays(), 0) && isDayCount(this.lateToleranceDays(), 0));
    return isDayCount(this.durationDays(), 1) && isDayCount(this.reminderDurationDays(), 0) && tolerancesValid;
  });

  readonly canSubmit = computed(() => !this.creatingTimer()
    && this.selectedTimerType() !== undefined
    && (this.selectedConfig() !== undefined || this.ownValuesValid()));

  constructor() {
    this.qrSessionService.session().then(session => this.session.set(session));
  }

  goBack() {
    window.history.back();
  }

  openPreview(imageSource: string): void {
    const album: IAlbum[] = [{
      src: imageSource,
      caption: 'Bildvorschau',
      thumb: imageSource,
    }];

    this.lightbox.open(album, 0);
  }

  fileName(attachment: AttachmentDetailDto): string {
    const name = attachment.fileName || 'Dokument';
    return attachment.fileEnding && !name.endsWith(`.${attachment.fileEnding}`) ? `${name}.${attachment.fileEnding}` : name;
  }

  openTimerForm(): void {
    const firstConfig = this.timerConfigs()[0] ?? this.defaultTimerConfigs()[0];
    this.selectTimerType((firstConfig?.timerType as TimerType | undefined) ?? CONTRACTOR_TIMER_TYPES[0]);
    this.timerFormOpen.set(true);
  }

  selectTimerType(type: TimerType): void {
    this.selectedTimerType.set(type);
    const defaults = this.defaultTimerConfigs().find(config => config.timerType === type);
    if (!defaults) {
      return;
    }
    this.durationDays.set(defaults.durationDays);
    this.reminderDurationDays.set(defaults.reminderDurationDays);
    this.earlyToleranceDays.set(defaults.earlyToleranceDays);
    this.lateToleranceDays.set(defaults.lateToleranceDays);
    this.imagesMandatory.set(defaults.imagesMandatory);
  }

  parseDays(value: string): number | undefined {
    return value === '' ? undefined : Number(value);
  }

  daysLeft(timer: QrMaintenanceTimerDto): number {
    const diff = new Date(timer.nextDate).getTime() - Date.now();
    return Math.ceil(diff / MILLISECONDS_PER_DAY);
  }

  cancelTimerForm(): void {
    this.timerFormOpen.set(false);
  }

  async createTimer(): Promise<void> {
    const buildingEquipmentId = this.buildingEquipmentId();
    const type = this.selectedTimerType();
    const creatorName = this.creatorName().trim();
    const contractor = this.isContractorSession();
    if (!buildingEquipmentId || !type || !this.canSubmit()) {
      return;
    }
    if (contractor && !creatorName) {
      this.toastrService.warning('Bitte geben Sie einen Namen an.', 'Name erforderlich');
      return;
    }
    const ownValues = this.selectedConfig() === undefined;
    const dto: QrMaintenanceTimerCreateDto = {
      buildingEquipmentId,
      type,
      creatorName: contractor ? creatorName : undefined,
      durationDays: ownValues ? this.durationDays() : undefined,
      reminderDurationDays: ownValues ? this.reminderDurationDays() : undefined,
      earlyToleranceDays: contractor ? undefined : this.earlyToleranceDays(),
      lateToleranceDays: contractor ? undefined : this.lateToleranceDays(),
      imagesMandatory: contractor ? undefined : this.imagesMandatory(),
    };
    this.creatingTimer.set(true);
    try {
      await firstValueFrom(this.buildingEquipmentQrCodeService.createMaintenanceTimer(dto));
      this.toastrService.success('Wartungstimer wurde angelegt.', 'Erfolg');
      this.timerFormOpen.set(false);
      this.timers.reload();
    } catch {
      /* no-op */
    } finally {
      this.creatingTimer.set(false);
    }
  }

  async createRepairTask(): Promise<void> {
    const equipment = this.buildingEquipment();
    if (!equipment) {
      return;
    }
    const selection: RepairTaskCreateDialogResult | undefined = await firstValueFrom(
      this.dialogService.open(RepairTaskCreateDialogComponent, {
        closeOnBackdropClick: false,
        context: {
          buildingId: '',
          initialTitle: equipment.equipment.name,
          initialDescription: '',
          contractorOptionsLoader: () =>
            this.repairTaskExternalQrService.findEquipmentContractorOptions(equipment.id),
          emptyContractorMessage: 'Kein passender Dienstleister zugewiesen.',
        },
      }).onClose,
    );
    if (!selection) {
      return;
    }
    this.creatingRepairTask.set(true);
    try {
      const created = await firstValueFrom(this.repairTaskExternalQrService.createForEquipment({
        buildingEquipmentId: equipment.id,
        contractorId: selection.contractor.id,
        title: selection.title,
        description: selection.description,
      }));
      const mail: QrRepairTaskSendDto | undefined = await firstValueFrom(
        this.dialogService.open(QrRepairTaskMailDialogComponent, {
          closeOnBackdropClick: false,
          context: { draft: created.mailDraft },
        }).onClose,
      );
      if (!mail) {
        this.toastrService.info('Der Reparaturauftrag wurde als Entwurf gespeichert', 'Nicht versendet');
        return;
      }
      await firstValueFrom(this.repairTaskExternalQrService.sendCreated(created.repairTaskId, mail));
      this.toastrService.success(`Reparaturauftrag an ${selection.contractor.name} gesendet`, 'Gesendet');
    } finally {
      this.creatingRepairTask.set(false);
    }
  }
}
